import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getDocumentEditorServerURL } from 'shared/constants/APIconstants';
import { getRegion } from 'shared/store/selectors/user.selector';
import { useDocumentEditor } from './use-document-editor';

const SCRIPT_ID = 'document-editor-api-script';

/**
 * Hook to dynamically load the Document Editor API script
 * @returns {{ loaded: boolean, error: Error | null, isSupported: boolean }}
 */
export function useDocumentEditorScript() {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  const { isSupported } = useDocumentEditor();
  const userRegion = useSelector(getRegion);
  const serverUrl = getDocumentEditorServerURL(userRegion);

  useEffect(() => {
    let isMounted = true;

    const handleLoad = () => {
      if (isMounted) {
        setLoaded(true);
        setError(null);
      }
    };

    const handleError = () => {
      if (isMounted) {
        setError(new Error('Failed to load Document Editor API script'));
        setLoaded(false);
      }
    };

    // If document editor is not supported (feature disabled or unsupported region), don't load the script
    if (!isSupported || !serverUrl) {
      setLoaded(false);
      return;
    }

    // Check if DocsAPI is already available
    if (window.DocsAPI) {
      setLoaded(true);
      return;
    }

    // Check if script is already in DOM
    const existingScript = document.getElementById(SCRIPT_ID);
    if (existingScript) {
      // Script exists, wait for it to load
      existingScript.addEventListener('load', handleLoad);
      existingScript.addEventListener('error', handleError);

      return () => {
        existingScript.removeEventListener('load', handleLoad);
        existingScript.removeEventListener('error', handleError);
      };
    }

    // Create and append script
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `${serverUrl}/web-apps/apps/api/documents/api.js?preload=placeholder`;
    script.async = true;

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);

    document.body.appendChild(script);

    return () => {
      isMounted = false;
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };
  }, [serverUrl, isSupported]);

  return { loaded, error, isSupported };
}
